<template>
  <div class="app-item">
    <h2 class="app-item__heading">Highlight {{ itemIndex + 1 }}</h2>
    <div class="field">
      <n-text-input
        v-model="value.url"
        label="Link URL"
        :errorMessage="getErrorMessage('url', 'Link URL')"
        @input="() => markAsTouched('url')"
      />
    </div>
    <div class="field">
      <n-text-input
        v-model="value.label"
        label="Link Label"
        :errorMessage="getErrorMessage('label', 'Link Label')"
        @input="() => markAsTouched('label')"
      />
    </div>
    <div class="field">
      <n-text-input
        v-model="value.description"
        label="Link Description"
        :errorMessage="getErrorMessage('description', 'Link Description')"
        @input="() => markAsTouched('description')"
      />
    </div>
    <div class="field">
      <n-text-input
        v-model="value.image"
        label="Link Image"
        :errorMessage="getErrorMessage('image', 'Link Image')"
        @input="() => markAsTouched('image')"
      />
    </div>
    <div class="app-item__actions">
      <n-button-group>
        <n-button variant="primary--outline" @click="removeItem" :disabled="totalItems === 1">Remove Item</n-button>
      </n-button-group>
    </div>
    <hr />
  </div>
</template>

<script lang="ts" setup>
import { ListAppItem } from './models/ListApp';
import { PropType } from 'vue';
import { useVModel } from '@vueuse/core';
import useVuelidate, { Validation } from '@vuelidate/core';
import { helpers, required, url } from '@vuelidate/validators';
import VuelidateUtil from '@shared/util/vuelidateUtil';

const props = defineProps({
  modelValue: {
    type: Object as PropType<ListAppItem>,
    required: true,
  },
  itemIndex: {
    type: Number,
    default: 0,
  },
  totalItems: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: ListAppItem): void;
  (e: 'remove'): void;
}>();
const value = useVModel(props, 'modelValue', emit);

const validations = {
  url: { required, url },
  label: { required },
  description: { required },
  image: { url },
};

const $v = useVuelidate(validations, value);

function markAsTouched(key: keyof ListAppItem) {
  const field = $v.value[key];
  if (field) {
    field.$touch();
  }
  emit('update:modelValue', value.value);
}

function getErrorMessage(fieldName: keyof ListAppItem, label: string): string | null {
  return VuelidateUtil.getErrorMessage($v, fieldName, label);
}

function removeItem() {
  emit('remove');
}
</script>

<style scoped lang="scss">
.app-item {
  &__heading {
    font-weight: 500;
    font-size: 1.2em;
    margin: 0.5rem 0;
  }
  &__actions {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
